<template>
	<div
		v-htmlclass="{'show--custom-dropdown': customDropdownOpen}"
		class="select-input"
		:class="{
			'select-input--disabled': disabled,
			'select-input--focused': focused,
			'select-input--error': error,
			'select-input--empty': !options.length,
			'select-input--no-selection': !value,
			'select-input--custom-dropdown-open': customDropdownOpen,
			'select-input--custom-dropdown-top': customDropdownTop,
		}"
		v-on="customDropdownEnabled ? {
			focus: onFocusCustom,
			blur: onBlurCustom,
			keydown: onKeydownCustom,
			click: onValueClickCustom,
		} : {}"
		:tabindex="customDropdownEnabled ? 0 : -1"
	>
		<div class="select-input__wrapper">
			<FormInput
				:value="computedValue"
				:label="label"
				:label-option="labelOption"
				:placeholder="label"
				readonly
				:error="error"
				ref="formInput"
			/>
			<ul
				ref="dropdown"
				class="select-input__dropdown"
				v-if="customDropdownEnabled"
			>
				<li
					ref="dropdownOption"
					v-for="(option, index) in options"
					:key="option.value"
					class="select-input__dropdown-option"
					:class="{
						'select-input__dropdown-option--disabled': option.disabled,
						'select-input__dropdown-option--selected': option.value === value,
						'select-input__dropdown-option--hover': index === customDropdownHover,
					}"
					:data-value="option.value"
					:disabled="option.disabled"
					v-html="option.label || option.value"
					@click.prevent="onOptionClickCustom($event, option)"
					@mouseover="onOptionHoverCustom($event, option, index)"
				/>
			</ul>
			<select
				v-if="!customDropdownEnabled"
				class="select-input__select"
				ref="input"
				:id="id"
				:name="name"
				:required="required"
				:disabled="disabled || readonly"
				:value="value"
				@change="onInput"
				@focus="onFocus"
				@blur="onBlur"
			>
				<option
					value=""
					disabled
					v-html="label"
				/>
				<option
					v-for="option in options"
					:key="option.value"
					:value="option.value"
					:disabled="option.disabled"
					v-html="option.label || option.value"
				/>
			</select>
		</div>
		<span
			class="form-input__hint | form-input-hint"
			v-if="hint"
			v-html="hint"
		/>
		<span
			class="form-input__error | form-input-error"
			v-if="error && errorMessage"
			v-html="errorMessage"
		/>
		<span
			class="form-input__success | form-input-success"
			v-if="successMessage"
			v-html="successMessage"
		/>
	</div>
</template>

<script>
import FormInput from './FormInput';
import {isIos, isAndroid, isTouch} from '@/utils/features';
import throttle from '@/utils/throttle';

export default {
	components: {
		FormInput,
	},
	props: {
		id: String,
		name: String,
		value: Number | String,
		required: Boolean,
		disabled: Boolean,
		readonly: Boolean,
		label: String,
		labelOption: String,
		options: Array,
		hint: String,
		error: Boolean,
		errorMessage: String,
		success: Boolean,
		successMessage: String,
	},
	data() {
		return {
			focused: false,
			customDropdownOpen: false,
			customDropdownTop: false,
			customDropdownHover: null,
			ignoreFollowingClick: false,
		}
	},
	computed: {
		computedValue() {
			const option = this.options.filter(option => option.value == this.value)[0];
			return option ? option.label || option.value : '';
		},
		customDropdownEnabled() {
			return !isIos && !isAndroid;
		}
	},
	model: {
		prop: 'value',
		event: 'input'
	},
	methods: {
		onInput(e) {
			this.$emit('input', e.target.value);
		},
		onFocus(e) {
			this.focused = true;
			this.$emit('focus', e);
		},
		onBlur(e) {
			this.focused = false;
			this.$emit('blur', e);
		},
		showCustomDropdown() {
			if (this.disabled) {
				return
			}
			const height = this.$el.clientHeight;
			const windowHeight = window.innerHeight;

			const offsetTop = this.$el.getBoundingClientRect().top;
			const offsetBottom = offsetTop + height;
      const maxDropdownHeight = windowHeight - offsetTop - height;

			this.$refs.dropdown.removeAttribute('style');

			if (!this.$refs.dropdownOption || !this.$refs.dropdownOption.length) {
				return;
			}

      const dropdownHeight = this.$refs.dropdown.clientHeight;
			const selectedItem = this.$refs.dropdownOption.filter(option => option.dataset.value == this.value)[0];

      // if (maxDropdownHeight / windowHeight > 0.5 || maxDropdownHeight >= dropdownHeight) {
      if (maxDropdownHeight >= 120 || maxDropdownHeight >= dropdownHeight) {
				this.customDropdownTop = false;
				this.$refs.dropdown.style.maxHeight = `${maxDropdownHeight}px`;
      }
      else {
				this.customDropdownTop = true;
				this.$refs.dropdown.style.maxHeight = `${offsetTop}px`;
      }
			this.customDropdownOpen = true;
			this.$refs.dropdown.scrollTop = 0;

			if (selectedItem) {
				this.$refs.dropdown.scrollTop = selectedItem.offsetTop;
			}
		},
		hideCustomDropdown() {
			this.customDropdownOpen = false;
			this.customDropdownHover = null;
		},
		onKeydownCustom(e) {
			if (this.disabled) {
				return
			}
			const char = String.fromCharCode(e.which).toLowerCase();
			let prev;
			let next;
			let matchingItems;
			let matchPosition;
			let selectedIndex = this.options.reduce((a, option, index) => {
					if (option.value == this.value) {
						return index;
					}
					return a;
				}, null);

			if (e.key === 'Escape') {
				e.preventDefault();
				e.stopPropagation();
				this.hideCustomDropdown();
			}
			else if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				e.stopPropagation();
				if (this.customDropdownHover !== null) {
					const selectedOption = this.options[this.customDropdownHover];
					if (!selectedOption.disabled) {
						this.$emit('input', this.options[this.customDropdownHover].value);
						this.hideCustomDropdown();
					}
				}
			}
			else if (e.key !== 'Tab' && !(e.ctrlKey||e.metaKey||e.altKey||e.shiftKey)) {
				e.preventDefault();
				e.stopPropagation();
				this.showCustomDropdown();
				this.ignoreFollowingClick = false;

				if (e.key === 'ArrowUp') {
					if (this.customDropdownHover === null) {
						this.customDropdownHover = selectedIndex !== null ? selectedIndex : 0;
					}
					else {
						this.customDropdownHover = Math.max(this.customDropdownHover - 1, 0);
					}
					this.$refs.dropdown.scrollTop = this.$refs.dropdownOption[this.customDropdownHover].offsetTop;
				}
				else if (e.key === 'ArrowDown') {
					if (this.customDropdownHover === null) {
						this.customDropdownHover = selectedIndex !== null ? selectedIndex : 0;
					}
					else {
						this.customDropdownHover = Math.min(this.customDropdownHover + 1, this.options.length - 1);
					}
					this.$refs.dropdown.scrollTop = this.$refs.dropdownOption[this.customDropdownHover].offsetTop;
				}
				else {
					matchingItems = [];

					matchingItems = this.options.reduce((a, option, index) => {
						if ((''+(option.label || option.value)).toLowerCase().charAt(0) == char && !option.disabled) {
							a.push(index);
						}
						return a;
					}, []);

					matchPosition = matchingItems.reduce((a, index) => {
						if (
								((matchingItems.includes(this.customDropdownHover) && this.customDropdownHover < index) ||
								(!matchingItems.includes(this.customDropdownHover) && this.customDropdownHover !== index)) &&
								a === null
						) {
							return index;
						}
						return a;
					}, null);

					if (matchPosition === null) {
						matchPosition = 0;
					}
					this.customDropdownHover = matchPosition;
					this.$refs.dropdown.scrollTop = this.$refs.dropdownOption[this.customDropdownHover].offsetTop;
				}
			}
		},
		onFocusCustom(e) {
			this.focused = true;
			this.ignoreFollowingClick = true;
			this.showCustomDropdown();
			this.$emit('focus', e);
		},
		onBlurCustom(e) {
			this.focused = false;
			this.hideCustomDropdown();
			this.$emit('blur', e);
		},
		onValueClickCustom(e) {
			if (this.$refs.formInput && e.target === this.$refs.formInput.$el) {
				if (this.customDropdownOpen && !this.ignoreFollowingClick) {
					this.hideCustomDropdown();
					this.$emit('blur', e);
				} else {
					this.showCustomDropdown();
				}
				this.ignoreFollowingClick = false;
			}
		},
		onOptionClickCustom(e, option) {
			if (!option.disabled) {
				this.hideCustomDropdown();
				this.$emit('input', option.value);
			}
		},
		onOptionHoverCustom(e, option, index) {
			this.customDropdownHover = index;
		},
		onWindowResize() {
			if (this.customDropdownOpen) {
				this.showCustomDropdown();
			}
		}
	},
	mounted() {
		this.boundWindowResize = throttle(this.onWindowResize.bind(this));
		window.addEventListener('scroll', this.boundWindowResize);
		window.addEventListener('resize', this.boundWindowResize);
	},
	destroy() {
		window.removeEventListener('scroll', this.boundWindowResize);
		window.removeEventListener('resize', this.boundWindowResize);
	}
}
</script>

<style>

</style>
